import {
	Component,
	Input,
	Output,
	EventEmitter,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AiGatewayService } from '../adapter/ai-gateway.service';

export interface SuggestionState {
	status: 'idle' | 'loading' | 'success' | 'error';
	prompt: string;
	originalCode: string | null;
	suggestedCode: string | null;
	explanation: string | null;
	changes: string[];
	errorMessage: string | null;
	canUndo: boolean;
}

export interface CodeAppliedEvent {
	suggestedCode: string;
	changes: string[];
}

@Component({
	selector: 'app-ai-code-suggestions',
	standalone: true,
	imports: [CommonModule, FormsModule, MatIconModule, MatProgressSpinnerModule],
	templateUrl: './ai-code-suggestions.component.html',
	styleUrls: ['./ai-code-suggestions.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiCodeSuggestionsComponent implements OnDestroy {
	@Input() code: string = '';
	@Input() language: string = 'plaintext';
	/** Custom prompt to append as ADDITIONAL RULES in the system prompt */
	@Input() customPrompt: string = '';

	@Output() codeApplied = new EventEmitter<CodeAppliedEvent>();
	@Output() codeReverted = new EventEmitter<string>();

	state: SuggestionState = {
		status: 'idle',
		prompt: '',
		originalCode: null,
		suggestedCode: null,
		explanation: null,
		changes: [],
		errorMessage: null,
		canUndo: false,
	};

	private destroy$ = new Subject<void>();

	constructor(
		private aiGateway: AiGatewayService,
		private cdr: ChangeDetectorRef,
	) {}

	onPromptChange(): void {
		// Hide changes container when user starts typing after a successful suggestion
		if (this.state.status === 'success') {
			this.state.status = 'idle';
			this.state.explanation = null;
			this.state.changes = [];
			this.state.canUndo = false;
			this.cdr.markForCheck();
		}
	}

	applySuggestion(): void {
		if (!this.state.prompt.trim() || this.state.status === 'loading') return;

		this.state.status = 'loading';
		this.state.errorMessage = null;
		this.state.originalCode = this.code;
		this.cdr.markForCheck();

		// Combine the user's prompt with any custom system rules
		const effectiveCustomPrompt = this.customPrompt
			? `${this.customPrompt}\n\nUser request: ${this.state.prompt}`
			: this.state.prompt;

		this.aiGateway
			.getCodeSuggestions({
				code: this.code,
				language: this.language,
				suggestionType: 'custom',
				customPrompt: effectiveCustomPrompt,
				includeExplanation: true,
			})
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (response) => {
					this.state.status = 'success';
					this.state.suggestedCode = response.suggestedCode;
					this.state.explanation = response.explanation || null;
					this.state.changes = response.changes;
					this.state.canUndo = true;
					this.codeApplied.emit({
						suggestedCode: response.suggestedCode,
						changes: response.changes,
					});
					this.cdr.markForCheck();
				},
				error: (error) => {
					this.state.status = 'error';
					this.state.errorMessage =
						error?.error?.userMessage || 'Failed to generate suggestions. Please try again.';
					this.state.originalCode = null;
					this.cdr.markForCheck();
				},
			});
	}

	undoSuggestion(): void {
		if (!this.state.canUndo || !this.state.originalCode) return;

		this.codeReverted.emit(this.state.originalCode);
		this.state.status = 'idle';
		this.state.canUndo = false;
		this.state.suggestedCode = null;
		this.state.explanation = null;
		this.state.changes = [];
		this.cdr.markForCheck();
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}
}
